import { discountPercent, escapeHtml } from '../format.js';
import { groupBySection, type MenuPage } from '../paginate.js';
import type { MenuItemContent, MenuSection } from '../types.js';
import {
  bilingual,
  description,
  image,
  price,
  raisedPrice,
  variantLabel,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * A deals board: every item is a big card with its photo, a huge price and a star-burst badge
 * when it is discounted. Built for combos and meal deals.
 */

const S = '.mb-t-combo-deals';

function card(ctx: RenderContext, item: MenuItemContent, section: MenuSection): string {
  const percent = discountPercent(item);
  const selling = percent === null ? item.price : item.discountPrice!;
  const label = ctx.showEn ? section.title : section.titleAr || section.title;
  const prices = item.variants.length
    ? `<div class="cd-sizes">${item.variants
        .map((v) => `<span><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <b dir="ltr">${price(ctx, v.price)}</b></span>`)
        .join('')}</div>`
    : `<div class="cd-pricerow">${raisedPrice(ctx, selling, 'cd-rp')}${percent === null ? '' : `<s class="cd-old" dir="ltr">${price(ctx, item.price)}</s>`}</div>`;
  return [
    '<div class="cd-card">',
    `<div class="cd-photo">${image(ctx, item.imageMediaId, 'cd-img')}`,
    label ? `<span class="cd-tag">${escapeHtml(label)}</span>` : '',
    percent !== null ? `<span class="cd-burst" dir="ltr"><small>SAVE</small><b>${percent}%</b></span>` : '',
    '</div>',
    '<div class="cd-body">',
    bilingual(ctx, item.title, item.titleAr, 'cd-name'),
    description(ctx, item, 'cd-desc'),
    prices,
    '</div></div>',
  ].join('');
}

export const comboDeals: MenuTemplate = {
  id: 'combo-deals',
  name: 'Combo deals',
  description: 'Big photo cards with huge prices and star-burst “save” badges. Made for burger meals and deals.',
  defaultTheme: {
    backgroundColor: '#150f0d',
    surfaceColor: '#231915',
    textColor: '#ffffff',
    primaryColor: '#ffd23f',
    accentColor: '#ef3b2c',
    fontId: 'bold',
    backgroundMediaId: null,
  },
  variants: [
    { id: 'flame', name: 'Flame', theme: {} },
    { id: 'fresh', name: 'Fresh', theme: { backgroundColor: '#f2f6f0', surfaceColor: '#ffffff', textColor: '#1d2b1f', primaryColor: '#1d2b1f', accentColor: '#2a9d43' } },
    { id: 'royal', name: 'Royal', theme: { backgroundColor: '#1c1033', surfaceColor: '#2a1a4a', primaryColor: '#ffd166', accentColor: '#f72585' } },
    { id: 'ocean', name: 'Ocean', theme: { backgroundColor: '#0b2545', surfaceColor: '#13315c', primaryColor: '#ffd166', accentColor: '#f4a259', fontId: 'street' } },
  ],
  cuisines: ['burgers'],
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    const capacity = 6;
    const entries = groupBySection(board).flatMap((g) => g.items.map((item) => ({ item, section: g.section })));
    const pages: MenuPage[] = [];
    for (let i = 0; i < entries.length; i += capacity) {
      const slice = entries.slice(i, i + capacity);
      // One block per item keeps each card's section label.
      pages.push({ columns: [slice.map(({ item, section }) => ({ section, items: [item], continued: false }))] });
    }
    void orientation;
    return pages.length ? pages : [{ columns: [[]] }];
  },

  render(ctx) {
    const { board } = ctx;
    const blocks = ctx.page.columns.flat();
    const header = [
      '<div class="cd-header">',
      bilingual(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'cd-heading'),
      bilingual(ctx, board.brand.name, board.brand.nameAr, 'cd-brand'),
      '</div>',
    ].join('');
    const cards = blocks.length
      ? `<div class="cd-grid" style="--n:${blocks.length}">${blocks.map((b) => card(ctx, b.items[0]!, b.section)).join('')}</div>`
      : '<div class="mb-empty">No deals on this board yet</div>';
    return header + cards;
  },

  css: `
${S}{padding:2.4em 3em 2.6em;background-image:radial-gradient(circle at 50% -10%,color-mix(in srgb,var(--mb-accent) 28%,transparent),transparent 55%)}
${S} .cd-header{display:flex;align-items:baseline;justify-content:space-between;gap:2em;margin-bottom:1.6em}
${S} .cd-heading{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:4em;text-transform:uppercase;
  color:var(--mb-primary);line-height:.95}
${S} .cd-heading.mb-2nd{font-size:2.4em}
${S} .cd-brand{font-family:var(--mb-font-script);font-size:2em;color:var(--mb-accent)}
${S} .cd-grid{flex:1;min-height:0;display:grid;gap:1.8em;grid-template-columns:repeat(3,minmax(0,1fr));grid-auto-rows:minmax(0,1fr)}
${S}.mb-portrait .cd-grid{grid-template-columns:repeat(2,minmax(0,1fr))}
${S} .cd-card{position:relative;display:flex;flex-direction:column;min-height:0;overflow:hidden;border-radius:1.4em;background:var(--mb-surface);
  box-shadow:0 1em 2.4em rgba(0,0,0,.35)}
${S} .cd-photo{position:relative;flex:1 1 55%;min-height:0}
${S} .cd-img{width:100%;height:100%;object-fit:cover;display:block}
${S} .cd-tag{position:absolute;top:.9em;inset-inline-start:.9em;background:var(--mb-bg);color:var(--mb-primary);font-weight:800;
  font-size:.85em;letter-spacing:.08em;text-transform:uppercase;padding:.3em .8em;border-radius:99em}
${S} .cd-burst{position:absolute;top:.4em;inset-inline-end:.6em;width:6.2em;height:6.2em;display:flex;flex-direction:column;
  align-items:center;justify-content:center;background:var(--mb-primary);color:var(--mb-bg);line-height:1;transform:rotate(12deg);
  clip-path:polygon(50% 0,61% 13%,77% 5%,80% 22%,97% 25%,89% 40%,100% 52%,87% 63%,93% 79%,76% 80%,70% 97%,56% 88%,42% 100%,34% 85%,18% 92%,17% 75%,1% 70%,10% 55%,0 41%,15% 32%,9% 16%,26% 15%,33% 0)}
${S} .cd-burst small{font-size:.75em;font-weight:800}
${S} .cd-burst b{font-family:var(--mb-font-heading);font-size:1.9em}
${S} .cd-body{padding:1em 1.3em 1.2em;display:flex;flex-direction:column;gap:.3em}
${S} .cd-name{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.9em;text-transform:uppercase;line-height:1;
  color:var(--mb-text)}
${S} .cd-name.mb-2nd{font-size:1.4em}
${S} .cd-desc{font-size:.9em;opacity:.75;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .cd-pricerow{display:flex;align-items:flex-end;gap:.8em;margin-top:.2em}
${S} .cd-rp{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);color:var(--mb-primary);line-height:.85;white-space:nowrap}
${S}.mb-v-fresh .cd-rp{color:var(--mb-accent)}
${S} .cd-rp-w{font-size:3.6em}
${S} .cd-rp-f{font-size:1.5em;vertical-align:top;position:relative;top:.2em}
${S} .cd-rp-s{font-size:1.2em;margin:0 .2em}
${S} .cd-old{opacity:.5;font-size:1.1em;margin-bottom:.3em}
${S} .cd-sizes{display:flex;gap:1.2em;flex-wrap:wrap;font-size:1.3em}
${S} .cd-sizes b{font-family:var(--mb-font-heading);color:var(--mb-primary)}
${S} .cd-sizes small{opacity:.75}
`,
};
